/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComboboxItem } from "@mantine/core"
import { useThrottledCallback } from "@mantine/hooks"
import { DefaultSingleSelectProps } from "../DefaultSingleSelect"
import { useDefaultSuspenseInfiniteQuery } from "../../utils/useDefaultSuspenseInfiniteQuery"
import { Pagination } from "../../utils/Pagination"
import { DefaultSingleSelect } from "../DefaultSingleSelect"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useSelectedItemFallback } from "../../utils/useSelectedItemFallback"

type ExtractType<TData> = TData extends Pagination<infer T> ? T : never

export interface SingleSelectFromInfiniteQueryProps<
  TQueryData extends ReturnType<
    typeof useDefaultSuspenseInfiniteQuery | typeof useDefaultInfiniteQuery
  >,
  TData = TQueryData extends ReturnType<
    | typeof useDefaultSuspenseInfiniteQuery<
        (...args: any[]) => Promise<any>,
        infer T extends Pagination<unknown>
      >
    | typeof useDefaultInfiniteQuery<
        (...args: any[]) => Promise<any>,
        infer T extends Pagination<unknown>
      >
  >
    ? ExtractType<T>
    : unknown,
> extends Omit<
    DefaultSingleSelectProps,
    "options" | "searchValue" | "onSearchChange"
  > {
  queryData: TQueryData
  entitiesToComboboxData: (data: TData) => ComboboxItem
  searchValue?: string
  onSearchChange?: (val: string) => void
  loading?: boolean
  /** Optional function to fetch a single item by ID. If provided, the selected item will be fetched separately and merged into options. */
  fetchSelectedItem?: (id: number | string) => Promise<TData | null>
  /** Maps a string ID to the React Query key for that entity — must match the key used by the corresponding dataloader/query hook to share the cache. */
  getSelectedItemQueryKey?: (id: string) => readonly unknown[]
}

export function SingleSelectFromInfiniteQuery<
  TQueryData extends ReturnType<
    typeof useDefaultSuspenseInfiniteQuery | typeof useDefaultInfiniteQuery
  >,
  TData = TQueryData extends ReturnType<
    | typeof useDefaultSuspenseInfiniteQuery<
        (...args: any[]) => Promise<any>,
        infer T extends Pagination<unknown>
      >
    | typeof useDefaultInfiniteQuery<
        (...args: any[]) => Promise<any>,
        infer T extends Pagination<unknown>
      >
  >
    ? ExtractType<T>
    : unknown,
>({
  searchValue,
  onSearchChange,
  entitiesToComboboxData,
  loading,
  queryData,
  fetchSelectedItem,
  getSelectedItemQueryKey,
  ...rest
}: SingleSelectFromInfiniteQueryProps<TQueryData, TData>) {
  const [internalSearchValue, setInternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(() => {
    setInternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } =
    queryData

  // Fetch selected item separately if a fetch function is provided
  const { selectedComboboxItems, isLoadingSelectedItems } =
    useSelectedItemFallback({
      selectedIds: rest.value,
      entitiesToComboboxData,
      fetchSelectedItem,
      getQueryKey: getSelectedItemQueryKey ?? ((id) => ["selectedItem", id]),
    })

  const throttledFetchNextPage = useThrottledCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, 300)

  const handleSearchValueChange = (newVal: string) => {
    onSearchChange?.(newVal)
    setInternalSearchValue(newVal)
  }

  // Merge paginated options with selected item
  // If the selected item is not in the current page, prepend it
  const paginatedOptions =
    data?.pages.flatMap((page) =>
      page?.data.flatMap((entity) => entitiesToComboboxData(entity))
    ) ?? []

  const mergedOptions = React.useMemo(() => {
    if (selectedComboboxItems.length === 0) {
      return paginatedOptions
    }

    // For single select, we only have one selected item
    const selectedItem = selectedComboboxItems[0]

    // Check if selected item is already in paginated options
    const exists = paginatedOptions.some(
      (opt) => opt.value === selectedItem.value
    )

    // If not in paginated options, prepend it
    return exists ? paginatedOptions : [selectedItem, ...paginatedOptions]
  }, [paginatedOptions, selectedComboboxItems])

  return (
    <DefaultSingleSelect
      searchValue={internalSearchValue}
      onSearchChange={handleSearchValueChange}
      options={mergedOptions}
      value={rest.value}
      loading={isLoading || isLoadingSelectedItems || loading}
      onScrolledToBottom={throttledFetchNextPage}
      {...rest}
    />
  )
}
